/** Typed fields for `Database/*.zwdoc` worldbuilding entries (#72). Mirrors
 * `src-tauri/src/entity_frontmatter.rs::EntityFrontmatter` field-for-field — Rust
 * owns parsing/serializing (via `serde_yaml_ng`) and is the only writer; this
 * module just gives the frontend a typed, defensively-normalized shape for
 * whatever comes back over IPC. */

import {
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
  matchMaybe,
  toMaybe,
} from "./maybe";

/** A custom field's value — a single string (text/longText/select) or a list of
 * strings (tags-shaped), matching `EntityFieldValue`'s untagged Rust enum. */
export type EntityFieldValue = string | string[];

/** A directed relationship to another `Database/*.zwdoc` entry (#72 Phase 2). */
export type EntityLink = {
  id: string;
  file: string;
  relation: string;
  note: Maybe<string>;
};

/** Manuscript scene target on an arc moment (`Chapters/<folder>/<scene>.md`). */
export type EntityArcLink = {
  file: string;
};

/** A single moment on a worldbuilding entry's arc (#72 Phase 4). */
export type EntityArcMoment = {
  id: string;
  order: number;
  at: Maybe<string>;
  moment: string;
  link: Maybe<EntityArcLink>;
};

export type EntityFrontmatter = {
  type: Maybe<string>;
  aka: string[];
  role: Maybe<string>;
  summary: Maybe<string>;
  image: Maybe<string>;
  tags: string[];
  color: Maybe<string>;
  status: Maybe<string>;
  plural: Maybe<string>;
  adjective: Maybe<string>;
  custom: Record<string, EntityFieldValue>;
  links: EntityLink[];
  arc: EntityArcMoment[];
  sceneLinks: EntityArcLink[];
  excludeFromScene: string[];
};

export function emptyEntityFrontmatter(): EntityFrontmatter {
  return {
    type: nothing,
    aka: [],
    role: nothing,
    plural: nothing,
    adjective: nothing,
    summary: nothing,
    image: nothing,
    tags: [],
    color: nothing,
    status: nothing,
    custom: {},
    links: [],
    arc: [],
    sceneLinks: [],
    excludeFromScene: [],
  };
}

function asMaybeNonEmptyString(value: unknown): Maybe<string> {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? just(trimmed) : nothing;
  }
  return nothing;
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

function normalizeFieldValue(value: unknown): Maybe<EntityFieldValue> {
  if (typeof value === "string") return just(value);
  if (Array.isArray(value)) {
    const list = asStringList(value);
    return list.length !== 0 ? just(list) : nothing;
  }
  return nothing;
}

function normalizeCustom(value: unknown): Record<string, EntityFieldValue> {
  if (value === null || typeof value !== "object") return {};
  const result: Record<string, EntityFieldValue> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const normalized = normalizeFieldValue(raw);
    if (isJust(normalized)) result[key] = normalized.value;
  }
  return result;
}

function normalizeLink(value: unknown): Maybe<EntityLink> {
  if (value === null || typeof value !== "object") return nothing;
  const obj = value as Record<string, unknown>;
  const id = typeof obj.id === "string" ? obj.id.trim() : "";
  const file = typeof obj.file === "string" ? obj.file.trim() : "";
  const relation = typeof obj.relation === "string" ? obj.relation.trim() : "";
  if (id.length === 0 || file.length === 0 || relation.length === 0) {
    return nothing;
  }
  return just({
    id,
    file,
    relation,
    note: asMaybeNonEmptyString(obj.note),
  });
}

function normalizeLinks(value: unknown): EntityLink[] {
  if (Array.isArray(value) === false) return [];
  const links: EntityLink[] = [];
  for (const entry of value) {
    const linkM = normalizeLink(entry);
    if (isJust(linkM)) links.push(linkM.value);
  }
  return links;
}

function normalizeArcLink(value: unknown): Maybe<EntityArcLink> {
  if (value === null || typeof value !== "object") return nothing;
  const fileM = asMaybeNonEmptyString((value as Record<string, unknown>).file);
  if (isNothing(fileM)) return nothing;
  return just({ file: fileM.value });
}

function normalizeArcMoment(value: unknown): Maybe<EntityArcMoment> {
  if (value === null || typeof value !== "object") return nothing;
  const obj = value as Record<string, unknown>;
  const id = typeof obj.id === "string" ? obj.id.trim() : "";
  const moment =
    typeof obj.moment === "string"
      ? obj.moment.trim()
      : typeof obj.beat === "string"
        ? obj.beat.trim()
        : "";
  const order =
    typeof obj.order === "number" && Number.isFinite(obj.order)
      ? obj.order
      : null;
  if (id.length === 0 || moment.length === 0 || order === null) return nothing;
  const linkRaw = obj.link;
  const linkM =
    linkRaw === null || typeof linkRaw === "undefined"
      ? nothing
      : normalizeArcLink(linkRaw);
  if (linkRaw !== null && typeof linkRaw !== "undefined" && isNothing(linkM)) {
    return nothing;
  }
  return just({
    id,
    order,
    at: asMaybeNonEmptyString(obj.at),
    moment,
    link: linkM,
  });
}

function normalizeArc(value: unknown): EntityArcMoment[] {
  if (Array.isArray(value) === false) return [];
  const moments: EntityArcMoment[] = [];
  for (const entry of value) {
    const momentM = normalizeArcMoment(entry);
    if (isJust(momentM)) moments.push(momentM.value);
  }
  return moments;
}

/** Defensive normalizer for whatever comes back over IPC — same role as
 * `normalizeZwConfig` for `.zwconfig`. Never throws; unknown/malformed input
 * degrades to `emptyEntityFrontmatter()`. */
export function normalizeEntityFrontmatter(raw: unknown): EntityFrontmatter {
  const base = emptyEntityFrontmatter();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  return {
    type: asMaybeNonEmptyString(obj.type),
    aka: asStringList(obj.aka),
    role: asMaybeNonEmptyString(obj.role),
    plural: asMaybeNonEmptyString(obj.plural),
    adjective: asMaybeNonEmptyString(obj.adjective),
    summary: asMaybeNonEmptyString(obj.summary),
    image: asMaybeNonEmptyString(obj.image),
    tags: asStringList(obj.tags),
    color: asMaybeNonEmptyString(obj.color),
    status: asMaybeNonEmptyString(obj.status),
    custom: normalizeCustom(obj.custom),
    links: normalizeLinks(obj.links),
    arc: normalizeArc(obj.arc),
    excludeFromScene: asStringList(obj.excludeFromScene),
    sceneLinks: Array.isArray(obj.sceneLinks)
      ? obj.sceneLinks.flatMap((value: unknown) => {
          const link = normalizeArcLink(value);
          return isJust(link) ? [link.value] : [];
        })
      : [],
  };
}

/** Wire shape for `EntityFrontmatter`: the inverse of
 * `normalizeEntityFrontmatter`. Nothing-valued fields are omitted so serde's
 * `Option<String>` (and the demo session) see an absent key, never a
 * `{ tag: "nothing" }` object. Call at the invoke boundary. */
export function entityFrontmatterToWire(
  fields: EntityFrontmatter,
): Record<string, unknown> {
  const optional = (key: string, value: Maybe<string>): [string, string][] =>
    matchMaybe(value, {
      just: (v) => [[key, v]],
      nothing: () => [],
    });
  return {
    ...Object.fromEntries([
      ...optional("type", fields.type),
      ...optional("role", fields.role),
      ...optional("plural", fields.plural),
      ...optional("adjective", fields.adjective),
      ...optional("summary", fields.summary),
      ...optional("image", fields.image),
      ...optional("color", fields.color),
      ...optional("status", fields.status),
    ]),
    aka: fields.aka,
    tags: fields.tags,
    custom: fields.custom,
    links: fields.links.map((link) => ({
      id: link.id,
      file: link.file,
      relation: link.relation,
      ...Object.fromEntries(optional("note", link.note)),
    })),
    sceneLinks: fields.sceneLinks,
    excludeFromScene: fields.excludeFromScene,
    arc: fields.arc.map((moment) => ({
      id: moment.id,
      order: moment.order,
      moment: moment.moment,
      ...Object.fromEntries(optional("at", moment.at)),
      ...matchMaybe(moment.link, {
        just: (link) => ({ link: { file: link.file } }),
        nothing: () => ({}),
      }),
    })),
  };
}

export function isEntityFrontmatterEmpty(fields: EntityFrontmatter): boolean {
  return (
    isNothing(fields.type) &&
    fields.aka.length === 0 &&
    isNothing(fields.role) &&
    isNothing(fields.plural) &&
    isNothing(fields.adjective) &&
    isNothing(fields.summary) &&
    isNothing(fields.image) &&
    fields.tags.length === 0 &&
    isNothing(fields.color) &&
    isNothing(fields.status) &&
    Object.keys(fields.custom).length === 0 &&
    fields.links.length === 0 &&
    fields.arc.length === 0 &&
    fields.sceneLinks.length === 0 &&
    fields.excludeFromScene.length === 0
  );
}

/** The v1 entity types from the plan — extensible, but these get first-class
 * treatment (create/filter/badge/peek). Anything else (including absent) is
 * untyped/legacy. */
export const DATABASE_ENTITY_TYPES = [
  { id: "character", label: "Character", color: "#7a4a52" },
  { id: "location", label: "Location", color: "#2a5c6a" },
  { id: "item", label: "Item", color: "#9a7b4f" },
  { id: "faction", label: "Faction", color: "#5c4a6a" },
  { id: "event", label: "Event", color: "#7a4a2e" },
] as const;

export type DatabaseEntityType = (typeof DATABASE_ENTITY_TYPES)[number]["id"];

export function isDatabaseEntityType(
  value: string,
): value is DatabaseEntityType {
  return DATABASE_ENTITY_TYPES.some((t) => t.id === value);
}

/** Human label for a type id — built-in title case, else the raw custom string. */
export function entityTypeLabel(type: Maybe<string>): Maybe<string> {
  return matchMaybe(type, {
    nothing: () => nothing,
    just: (typeId) => {
      const builtIn = DATABASE_ENTITY_TYPES.find((t) => t.id === typeId);
      return toMaybe(builtIn?.label ?? typeId);
    },
  });
}
